<template>
  <span>
    <transition :name="transition" @after-enter="handleAfterEnter" @after-leave="handleAfterLeave">
      <div
        class="el-popover el-popper"
        :class="[popperClass, 'el-popover--plain']"
        ref="popper"
        v-show="!disabled && showPopper"
        :style="{ width: width + 'px' }"
        role="tooltip"
        :id="tooltipId"
        :aria-hidden="disabled || !showPopper ? 'true' : 'false'"
      >
        <div class="el-popover__title">{{ $t('table.title') }}</div>
        <vxe-toolbar>
          <template v-slot:buttons>
            <vxe-button
              type="primary"
              icon="el-icon-plus"
              class="vxe-btn-primary"
              @click="insertEvent()"
              >{{ $t('xTable.insert') }}</vxe-button
            >
            <vxe-button
              type="primary"
              icon="el-icon-delete"
              class="vxe-btn-delete"
              @click="deleteEvent()"
              >{{ $t('xTable.delete') }}</vxe-button
            >
          </template>
        </vxe-toolbar>
        <vxe-grid
          border
          size="mini"
          align="left"
          show-overflow
          show-header-overflow
          ref="xTable"
          class="vxe-table-element"
          resizable
          show-footer
          :auto-resize="true"
          :data="tableData"
          :valid-config="{ autoPos: true, message: 'inline' }"
          :edit-config="{ trigger: 'click', mode: 'row' }"
        >
          <vxe-table-column type="checkbox" width="50"></vxe-table-column>
          <vxe-table-column
            field="name"
            :title="$t('table.name')"
            :edit-render="{ type: 'default' }"
          >
            <template v-slot:edit="scope">
              <el-input
                v-model="scope.row.name"
                :maxlength="maxlength || 32"
                @input="(val) => changeMultipleValue(val, scope.row, scope)"
              ></el-input>
            </template>
          </vxe-table-column>
        </vxe-grid>
        <div class="dialog-footer multiple-footer">
          <el-button type="primary" @click="addSure()">{{ $t('dialog.confirm') }}</el-button>
          <el-button @click="showPopper = false">{{ $t('dialog.cancel') }}</el-button>
        </div>
      </div>
    </transition>
    <i ref="reference" style="cursor: pointer; font-size: 16px" class="el-icon-plus"></i>
  </span>
</template>
<script>
  import Popper from 'element-ui/src/utils/vue-popper';
  import { on, off } from 'element-ui/src/utils/dom';
  import { addClass, removeClass } from 'element-ui/src/utils/dom';
  import { generateId } from 'element-ui/src/utils/util';

  export default {
    name: 'ElPopover',

    mixins: [Popper],

    props: {
      trigger: {
        type: String,
        default: 'click',
        validator: (value) => ['click', 'focus', 'hover', 'manual'].indexOf(value) > -1,
      },
      openDelay: {
        type: Number,
        default: 0,
      },
      closeDelay: {
        type: Number,
        default: 200,
      },
      disabled: Boolean,
      popperClass: String,
      width: {
        type: Number,
        default: 320,
      },
      visibleArrow: {
        default: true,
      },
      arrowOffset: {
        type: Number,
        default: 0,
      },
      transition: {
        type: String,
        default: 'fade-in-linear',
      },
      tabindex: {
        type: Number,
        default: 0,
      },
      //输入框最大可输入
      maxlength: {
        type: [String, Number],
        default: () => '64',
      },
      defaultValue: {
        type: String,
        default: '',
      },
    },

    computed: {
      tooltipId() {
        return `el-popover-${generateId()}`;
      },
    },
    watch: {
      showPopper(val) {
        if (this.disabled) {
          return;
        }
        val ? this.$emit('show') : this.$emit('hide');
      },
      defaultValue(val) {
        //防止输入的是中文逗号
        val = val.replace(/，/gi, ',');
        let value = val.split(',');
        let list = [];
        //剔除空数据
        value.forEach((item) => {
          if (item) {
            list.push({ name: item });
          }
        });
        this.tableData = list;
      },
    },
    data() {
      const vm = window.vm;
      return {
        vm: vm,
        tableData: [],
      };
    },
    mounted() {
      let reference = (this.referenceElm = this.reference || this.$refs.reference);
      const popper = this.popper || this.$refs.popper;

      if (!reference && this.$slots.reference && this.$slots.reference[0]) {
        reference = this.referenceElm = this.$slots.reference[0].elm;
      }
      // 可访问性
      if (reference) {
        addClass(reference, 'el-popover__reference');
        reference.setAttribute('aria-describedby', this.tooltipId);
        reference.setAttribute('tabindex', this.tabindex); // tab序列
        popper.setAttribute('tabindex', 0);

        on(reference, 'keydown', this.handleKeydown);
        on(reference, 'click', this.handleClick);
      }
      if (this.trigger === 'click') {
        on(reference, 'click', this.doToggle);
        on(document, 'click', this.handleDocumentClick);
        on(document, 'mousedown', this.handleDocumentClick);
      }
    },

    beforeDestroy() {
      this.cleanup();
    },

    deactivated() {
      this.cleanup();
    },

    methods: {
      doToggle() {
        this.showPopper = !this.showPopper;
      },
      doShow() {
        this.showPopper = true;
      },
      doClose() {
        this.showPopper = false;
      },
      handleFocus() {
        addClass(this.referenceElm, 'focusing');
        if (this.trigger === 'click' || this.trigger === 'focus') this.showPopper = true;
      },
      handleClick() {
        removeClass(this.referenceElm, 'focusing');
      },
      handleBlur() {
        removeClass(this.referenceElm, 'focusing');
        if (this.trigger === 'click' || this.trigger === 'focus') this.showPopper = false;
      },
      handleKeydown(ev) {
        if (ev.keyCode === 27 && this.trigger !== 'manual') {
          // esc
          this.doClose();
        }
      },
      handleDocumentClick(e) {
        let reference = this.reference || this.$refs.reference;
        const popper = this.popper || this.$refs.popper;

        if (!reference && this.$slots.reference && this.$slots.reference[0]) {
          reference = this.referenceElm = this.$slots.reference[0].elm;
        }
        if (
          !this.$el ||
          !reference ||
          this.$el.contains(e.target) ||
          reference.contains(e.target) ||
          !popper ||
          popper.contains(e.target)
        )
          return;
        this.showPopper = false;
      },
      handleAfterEnter() {
        this.$emit('after-enter');
      },
      handleAfterLeave() {
        this.$emit('after-leave');
        this.doDestroy();
      },
      cleanup() {
        if (this.openDelay || this.closeDelay) {
          clearTimeout(this._timer);
        }
      },
      changeMultipleValue(value, row, scope) {
        value = value.toString().replace(/\，/g, '');
        value = value.toString().replace(/\,/g, '');
        row.name = value;
        this.$refs.xTable.reloadRow(row);
        this.$refs.xTable.updateStatus(scope);
      },
      //新增一行
      insertEvent() {
        const record = { name: null };
        this.$refs.xTable.insert(record).then(({ row }) => this.$refs.xTable.setActiveRow(row));
      },
      //删除行
      deleteEvent() {
        let selectRecords = this.$refs.xTable.getCheckboxRecords();
        if (selectRecords.length) {
          this.$refs.xTable.removeSelecteds();
        } else {
          this.$message({
            showClose: true,
            message: this.$t('xTable.noSelectData'),
            type: 'error',
          });
        }
      },
      addSure() {
        const getTableData = this.$refs.xTable.getTableData().fullData;
        let multiple = [];
        getTableData.forEach((item) => {
          if (item.name) {
            multiple.push(item.name);
          }
        });
        multiple = multiple.join(',');
        this.$emit('setMultiple', multiple);
        this.doClose();
      },
    },

    destroyed() {
      const reference = this.reference;

      off(reference, 'click', this.doToggle);
      off(reference, 'mouseup', this.doClose);
      off(reference, 'mousedown', this.doShow);
      off(reference, 'focusin', this.doShow);
      off(reference, 'focusout', this.doClose);
      off(reference, 'mousedown', this.doShow);
      off(reference, 'mouseup', this.doClose);
      off(reference, 'mouseleave', this.handleMouseLeave);
      off(reference, 'mouseenter', this.handleMouseEnter);
      off(document, 'click', this.handleDocumentClick);
    },
  };
</script>
<style lang="scss" scoped>
  .el-popper {
    z-index: 9999 !important;
  }
  .multiple-footer {
    margin: 20px 0 10px 0px;
  }
</style>
